import { useRef, useState, type DragEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { sendFile } from '../lib/upload';
import { fileSize, hostOf, plural } from '../lib/format';
import { useDocumentTitle, useLoad, useProjectEvents } from '../lib/hooks';
import type { Item, Project } from '../lib/types';
import { useAuth } from '../state/auth';
import { AppShell } from '../components/AppShell';
import { Icon, type IconName } from '../components/Icon';
import { ConfirmDialog, CopyButton, Dialog, Empty, ErrorState, Field, Menu, PageLoading } from '../components/ui';
import { useToast } from '../components/toast';
import { AccessTab } from './project/AccessTab';
import { ActivityTab } from './project/ActivityTab';
import { DiscussionTab } from './project/DiscussionTab';
import { FilesTab } from './project/FilesTab';
import { HandoffTab } from './project/HandoffTab';

type TabId = 'review' | 'discussion' | 'files' | 'handoff' | 'access' | 'activity';

const TYPE_META: Record<Item['type'], { icon: IconName; label: string }> = {
  website: { icon: 'globe', label: 'Website' },
  image: { icon: 'image', label: 'Image' },
  video: { icon: 'video', label: 'Video' },
};

function ItemCard({ item, projectId }: { item: Item; projectId: string }) {
  const meta = TYPE_META[item.type];
  const asset = item.currentVersion?.asset;
  return (
    <Link to={`/projects/${projectId}/items/${item.id}`} className="item-card">
      <div className="item-thumb">
        {item.type === 'image' && asset ? (
          <img src={asset.url} alt="" loading="lazy" />
        ) : item.type === 'video' && asset ? (
          <video src={`${asset.url}#t=0.5`} preload="metadata" muted playsInline tabIndex={-1} />
        ) : (
          <div className="thumb-host">
            <Icon name="globe" size={28} />
            <span>{hostOf(item.url)}</span>
          </div>
        )}
        <span className="badge thumb-type">
          <Icon name={meta.icon} size={12} /> {meta.label}
        </span>
      </div>
      <div className="item-body">
        <span className="item-title truncate">{item.title}</span>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {item.counts.open > 0 ? <span className="badge badge-open">{item.counts.open} open</span> : <span className="badge">No open revisions</span>}
          {item.reviewStatus === 'approved' && (
            <span className="badge badge-resolved">
              <Icon name="check" size={12} /> Approved
            </span>
          )}
          {item.reviewStatus === 'changes_requested' && <span className="badge badge-progress">Changes requested</span>}
          {item.versions && item.versions.length > 1 && <span className="badge">v{item.currentVersion?.versionNo}</span>}
        </div>
      </div>
    </Link>
  );
}

function AddItemDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const navigate = useNavigate();
  const { meta } = useAuth();
  const [kind, setKind] = useState<'website' | 'media'>('website');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [over, setOver] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const busy = progress !== null;

  const pick = (candidate: File | undefined | null) => {
    if (!candidate) return;
    if (!/^(image|video)\//.test(candidate.type)) {
      setError('Review items are images or videos. Put other files in the Files tab.');
      return;
    }
    setError(null);
    setFile(candidate);
    if (!title) setTitle(candidate.name.replace(/\.[^.]+$/, ''));
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setOver(false);
    pick(event.dataTransfer.files[0]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setProgress(0);
    try {
      let item: Item;
      if (kind === 'website') {
        ({ item } = await api.post<{ item: Item }>(`/api/projects/${project.id}/items`, { type: 'website', title, url }));
      } else {
        if (!file) throw new Error('Choose an image or video first.');
        ({ item } = await sendFile<{ item: Item }>({ projectId: project.id, purpose: 'item', file, title, chunkBytes: meta?.uploadChunkBytes ?? 32 * 1024 * 1024, onProgress: setProgress }));
      }
      navigate(`/projects/${project.id}/items/${item.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setProgress(null);
    }
  };

  return (
    <Dialog title="Add something to review" description="Reviewers pin their revisions directly onto it." onClose={onClose} dismissible={!busy}>
      <form className="stack" style={{ gap: 16 }} onSubmit={submit}>
        <div className="choice-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <button type="button" className="choice" aria-pressed={kind === 'website'} onClick={() => setKind('website')}>
            <Icon name="globe" className="icon-lg" />
            Website or page
          </button>
          <button type="button" className="choice" aria-pressed={kind === 'media'} onClick={() => setKind('media')}>
            <Icon name="image" className="icon-lg" />
            Image or video
          </button>
        </div>

        {kind === 'website' ? (
          <Field label="Address" hint="Any live, staging or password-protected site: Shopify, WordPress, Webflow, Wix, Squarespace, Netlify previews.">
            {(id) => <input id={id} className="input" placeholder="staging.acme.com" value={url} onChange={(e) => setUrl(e.target.value)} required autoCapitalize="none" spellCheck={false} />}
          </Field>
        ) : (
          <div
            className={`dropzone ${over ? 'is-over' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
          >
            <input ref={fileInput} type="file" accept="image/*,video/*" hidden onChange={(event) => pick(event.target.files?.[0])} />
            <Icon name="upload" className="icon-lg" />
            {file ? (
              <strong style={{ color: 'var(--text-primary)' }}>
                {file.name} · {fileSize(file.size)}
              </strong>
            ) : (
              <span>Drop an image or video here</span>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileInput.current?.click()}>
              {file ? 'Choose a different file' : 'Choose a file'}
            </button>
            <span style={{ fontSize: 'var(--fs-xs)' }}>PNG, JPG, WebP, GIF, SVG, MP4, WebM or MOV, up to {meta?.maxUploadMb ?? 1024} MB</span>
          </div>
        )}

        <Field label="Title" hint={kind === 'website' ? 'Optional. Defaults to the site address.' : undefined}>
          {(id) => <input id={id} className="input" placeholder={kind === 'website' ? 'Staging site' : 'Homepage hero, launch video…'} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>

        {busy && kind === 'media' && (
          <div className="progress" role="progressbar" aria-valuenow={Math.round((progress ?? 0) * 100)} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${(progress ?? 0) * 100}%` }} />
          </div>
        )}
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy || (kind === 'media' && !file)}>
            {busy && <span className="spinner" />}
            {kind === 'website' ? 'Add website' : 'Upload and open'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EditProjectDialog({ project, onClose, onSaved }: { project: Project; onClose: () => void; onSaved: (project: Project) => void }) {
  const [name, setName] = useState(project.name);
  const [clientName, setClientName] = useState(project.clientName || '');
  const [description, setDescription] = useState(project.description || '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api.patch<{ project: Project }>(`/api/projects/${project.id}`, { name, clientName, description });
      onSaved(result.project);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog title="Project details" onClose={onClose}>
      <form className="stack" style={{ gap: 16 }} onSubmit={submit}>
        <Field label="Project name">{(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
        <Field label="Client">{(id) => <input id={id} className="input" value={clientName} onChange={(e) => setClientName(e.target.value)} />}</Field>
        <Field label="Notes">{(id) => <textarea id={id} className="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy}>
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function ProjectPage() {
  const { projectId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { isStaff } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<'archive' | 'delete' | null>(null);

  const { data, setData, error, loading, reload } = useLoad(() => api.get<{ project: Project; items: Item[] }>(`/api/projects/${projectId}`), [projectId]);
  useProjectEvents(data?.project.id, () => void reload(true));
  useDocumentTitle(data?.project.name);

  if (loading && !data) return <PageLoading label="Opening project" />;
  if (error || !data) {
    return (
      <AppShell crumbs={[{ label: 'Projects', to: '/' }]}>
        <ErrorState title="This project is not available" message={error || 'It may have been removed, or you may not have access.'} onRetry={() => reload()} />
      </AppShell>
    );
  }

  const { project, items } = data;
  const canManage = !!project.permissions?.canManage;
  const canComment = !!project.permissions?.canComment;
  const tabs: { id: TabId; label: string; count?: number; show: boolean }[] = [
    { id: 'review', label: 'Review', count: items.length, show: true },
    { id: 'discussion', label: 'Discussion', show: true },
    { id: 'files', label: 'Files', count: project.counts.files, show: true },
    { id: 'handoff', label: 'AI handoff', show: true },
    { id: 'access', label: 'Access', count: project.counts.members, show: canManage },
    { id: 'activity', label: 'Activity', show: canManage },
  ];
  const requested = params.get('tab') as TabId | null;
  const tab = tabs.find((t) => t.id === requested && t.show)?.id ?? 'review';
  const setTab = (id: TabId) => setParams(id === 'review' ? {} : { tab: id }, { replace: true });

  return (
    <AppShell crumbs={[{ label: 'Projects', to: '/' }, { label: project.name }]}>
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <h1>{project.name}</h1>
            {project.status === 'archived' && <span className="badge">Archived</span>}
          </div>
          <p>
            {[project.clientName, `${project.counts.openComments} open`, `${project.counts.resolvedComments} resolved`].filter(Boolean).join(' · ')}
            {project.description ? ` · ${project.description}` : ''}
          </p>
        </div>
        <div className="row">
          {canManage && <CopyButton text={project.shareUrl} label="Share for revisions" className="btn btn-secondary" />}
          {canManage && (
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              <Icon name="plus" /> Add item
            </button>
          )}
          {canManage && (
            <Menu
              label="Project actions"
              trigger={<Icon name="more" />}
              items={[
                { label: 'Edit details', icon: 'pencil', onSelect: () => setEditing(true) },
                { label: project.status === 'archived' ? 'Restore project' : 'Archive project', icon: 'folder', onSelect: () => setConfirm('archive') },
                ...(isStaff ? [{ label: 'Delete project', icon: 'trash' as IconName, danger: true, onSelect: () => setConfirm('delete') }] : []),
              ]}
            />
          )}
        </div>
      </div>

      <div className="tabs" role="tablist">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button key={t.id} type="button" role="tab" className="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
              {t.count !== undefined && <span className="count">{t.count}</span>}
            </button>
          ))}
      </div>

      <div className="section" role="tabpanel">
        {tab === 'review' &&
          (items.length === 0 ? (
            <Empty
              icon="pin"
              title="Nothing to review yet"
              action={
                canManage ? (
                  <button className="btn btn-primary" onClick={() => setAdding(true)}>
                    <Icon name="plus" /> Add item
                  </button>
                ) : undefined
              }
            >
              {canManage ? 'Add a website address, or upload an image or video. Then send the client the link and ask for their revisions.' : 'MK-Way has not added anything for review yet. You can already share files in the Files tab.'}
            </Empty>
          ) : (
            <>
              <span className="muted">{plural(items.length, 'item')} · open one and pin your revisions onto it.</span>
              <div className="grid-cards">
                {items.map((item) => (
                  <ItemCard key={item.id} item={item} projectId={project.id} />
                ))}
              </div>
            </>
          ))}
        {tab === 'discussion' && <DiscussionTab project={project} canComment={canComment} canManage={canManage} />}
        {tab === 'files' && <FilesTab project={project} canComment={canComment} canManage={canManage} onChanged={() => void reload(true)} />}
        {tab === 'handoff' && <HandoffTab project={project} items={items} />}
        {tab === 'access' && canManage && <AccessTab project={project} onProjectChange={(next) => setData((current) => current && { ...current, project: next })} />}
        {tab === 'activity' && canManage && <ActivityTab project={project} />}
      </div>

      {adding && <AddItemDialog project={project} onClose={() => setAdding(false)} />}
      {editing && (
        <EditProjectDialog
          project={project}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setData((current) => current && { ...current, project: next });
            setEditing(false);
          }}
        />
      )}
      {confirm === 'archive' && (
        <ConfirmDialog
          title={project.status === 'archived' ? 'Restore this project?' : 'Archive this project?'}
          message={project.status === 'archived' ? 'It moves back to the active list.' : 'It moves to the Archived list and project-password sign-in stops working. Nothing is deleted.'}
          confirmLabel={project.status === 'archived' ? 'Restore' : 'Archive'}
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            try {
              const result = await api.patch<{ project: Project }>(`/api/projects/${project.id}`, { status: project.status === 'archived' ? 'active' : 'archived' });
              setData((current) => current && { ...current, project: result.project });
            } catch (err) {
              toast.error(errorMessage(err));
            }
          }}
        />
      )}
      {confirm === 'delete' && (
        <ConfirmDialog
          title={`Delete "${project.name}"?`}
          message="Every review item, revision and uploaded file in this project is removed permanently. This cannot be undone."
          confirmLabel="Delete project"
          danger
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            try {
              await api.del(`/api/projects/${project.id}`);
              navigate('/');
            } catch (err) {
              toast.error(errorMessage(err));
            }
          }}
        />
      )}
    </AppShell>
  );
}
